import { Link, useParams } from 'react-router-dom';

import { useProductDetail, useRescan } from '../api/queries';
import { OfferBars } from '../charts/OfferBars';
import { PriceHistoryChart } from '../charts/PriceHistoryChart';
import { DIVERGE, MATCH_COLOR, MATCH_LABEL, SERIES, STATUS } from '../charts/palette';
import { Badge } from '../components/Badge';
import { Card } from '../components/Card';
import { CopyButton } from '../components/CopyButton';
import { Delta } from '../components/Delta';
import { PositionBadge } from '../components/PositionBadge';
import { Failed, Loading } from '../components/States';
import { ShopName } from '../components/ShopName';
import { StatTile } from '../components/StatTile';
import { count, money, when } from '../utils/format';

export function ProductDetailPage() {
  const { id } = useParams();
  const detail = useProductDetail(Number(id));
  const rescan = useRescan();

  if (detail.isLoading) {
    return <Loading label="Loading product…" />;
  }

  if (detail.isError) {
    return <Failed error={detail.error} />;
  }

  if (!detail.data) {
    return <div className="state">Product not found.</div>;
  }

  const { product, offers, scanHistory, ourPriceHistory } = detail.data;

  return (
    <div className="stack">
      <header className="page__head">
        <div>
          <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>
            <Link to="/products">← Products</Link>
          </div>
          <h1 className="page__title">{product.name}</h1>
          <p className="page__lede">
            EAN {product.ean ?? '—'}
            {product.ean && (
              <>
                {' '}
                <CopyButton value={product.ean} label="EAN" />
              </>
            )}{' '}
            · feed id #{product.itemId}
            {product.heurekaBrand && ` · ${product.heurekaBrand}`}
            {product.heurekaCategory && ` · ${product.heurekaCategory}`}
          </p>
        </div>
        <div className="row">
          <PositionBadge position={product.position} />
          {product.matchStatus && (
            <Badge
              label={MATCH_LABEL[product.matchStatus] ?? product.matchStatus}
              color={MATCH_COLOR[product.matchStatus]}
              title={product.confidence === null ? undefined : `confidence ${Number(product.confidence).toFixed(2)}`}
            />
          )}
          <a className="button" href={product.url} target="_blank" rel="noreferrer">
            Your shop ↗
          </a>
          {product.heurekaUrl && (
            <a className="button" href={product.heurekaUrl} target="_blank" rel="noreferrer">
              Heureka ↗
            </a>
          )}
          <button
            type="button"
            className="button button--primary"
            disabled={rescan.isPending}
            onClick={() => rescan.mutate(product.id)}
          >
            {rescan.isPending ? 'Queueing…' : 'Rescan now'}
          </button>
        </div>
      </header>

      <div className="grid grid--tiles">
        <StatTile label="Your price" value={money(product.ourPrice)} accent={SERIES[1]} />
        <StatTile
          label="Market low"
          value={money(product.minPrice)}
          foot={<ShopName product={product} />}
          accent={DIVERGE.low}
        />
        <StatTile
          label="Gap to market low"
          value={<Delta value={product.priceGapPct} />}
          foot={money(product.priceGapAbs)}
          accent={product.priceGapPct !== null && product.priceGapPct > 0 ? STATUS.critical : STATUS.good}
        />
        <StatTile
          label="Your rank"
          value={product.ourRank === null ? '—' : `#${product.ourRank}`}
          foot={`${count(product.offerCount)} ${product.offerCount === 1 ? 'offer' : 'offers'} on the listing`}
          accent={SERIES[6]}
        />
        <StatTile label="Market mean" value={money(product.avgPrice)} foot={`median ${money(product.medianPrice)}`} />
        <StatTile label="Market high" value={money(product.maxPrice)} foot={`scanned ${when(product.scannedAt)}`} />
      </div>

      <Card title="Current offers" note="Every shop on the Heureka listing at the newest scan">
        <OfferBars offers={offers} ourPrice={Number(product.ourPrice)} />
      </Card>

      <Card title="Price over time" note="Your price against the market, one point per week. Hover for how many scans a week holds.">
        <PriceHistoryChart history={scanHistory} />
      </Card>

      {/* Seven columns do not fit a half-width card without clipping, so the
          offer table gets the full row and the price history follows it. */}
      <div className="stack">
        <Card title="Offer detail" note={`${offers.length} offers`}>
          {offers.length === 0 ? (
            <div className="state">No offers captured.</div>
          ) : (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Shop</th>
                    <th className="num">Price</th>
                    <th className="num">Shipping</th>
                    <th className="num">Total</th>
                    <th>Stock</th>
                    <th className="num">Rating</th>
                  </tr>
                </thead>
                <tbody>
                  {offers.map((offer) => (
                    <tr key={offer.id}>
                      <td className="num">{offer.position}</td>
                      <td>
                        {offer.shopName}
                        {offer.isOurShop && (
                          <>
                            {' '}
                            <Badge label="you" color={SERIES[1]} />
                          </>
                        )}
                        {offer.isTopOffer && (
                          <>
                            {' '}
                            <Badge label="promoted" />
                          </>
                        )}
                      </td>
                      <td className="num">{money(offer.price)}</td>
                      <td className="num">{money(offer.shippingPrice)}</td>
                      <td className="num">{money(offer.totalPrice)}</td>
                      <td className="secondary">{offer.availability ?? '—'}</td>
                      <td className="num secondary">
                        {offer.shopRatingPercent === null ? '—' : `${offer.shopRatingPercent}%`}
                        {offer.shopRatingCount !== null && (
                          <div className="cell-product__meta">{count(offer.shopRatingCount)}×</div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Card title="Your price history" note="Every change seen in the feed">
          {ourPriceHistory.length === 0 ? (
            <div className="state">No price change recorded yet.</div>
          ) : (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>When</th>
                    <th className="num">Price</th>
                  </tr>
                </thead>
                <tbody>
                  {[...ourPriceHistory].reverse().map((point) => (
                    <tr key={point.id}>
                      <td className="secondary">{when(point.recordedAt)}</td>
                      <td className="num">{money(Number(point.priceVat))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
